import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useProjects } from '../hooks/useProjects';
import { useUnreadMessages } from '../hooks/useUnreadMessages';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menu,
  X,
  LogOut,
  Rocket,
  LayoutDashboard,
  Plus,
  FolderOpen,
  Briefcase,
  MessageCircle,
  HelpCircle,
  ChevronRight,
  Store,
  ShoppingBag,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { cn } from '../lib/utils';
import { useReduceMotion, useIsMobile } from '../utils/performance';

const Navbar = () => {
  const { user, logout } = useAuth();
  const { projects } = useProjects();
  const { hasUnread } = useUnreadMessages(!!user);
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const reduceMotion = useReduceMotion();
  const isMobile = useIsMobile();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  // Show "New Request" in nav only until they've created their first request
  const allNavLinks = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/new-request', label: 'New Request', icon: Plus },
    { path: '/requests', label: 'Message', icon: MessageCircle },
    { path: '/my-projects', label: 'My Projects', icon: FolderOpen },
    { path: '/marketplace', label: 'Marketplace', icon: Store },
    { path: '/my-purchases', label: 'Purchases', icon: ShoppingBag },
    { path: '/portfolio', label: 'Portfolio', icon: Briefcase },
    { path: '/support', label: 'Support', icon: HelpCircle },
  ];
  const navLinks = user && projects.length > 0
    ? allNavLinks.filter((link) => link.path !== '/new-request')
    : allNavLinks;

  return (
    <motion.nav
      initial={reduceMotion || isMobile ? { opacity: 0.98 } : { y: -100 }}
      animate={{ y: 0, opacity: 1 }}
      transition={reduceMotion ? { duration: 0.1 } : { type: 'spring', stiffness: 200, damping: 25 }}
      className={cn(
        'fixed top-0 left-0 right-0 z-50 safe-top',
        'transition-all duration-300 ease-out',
        scrolled
          ? 'bg-white/95 backdrop-blur-xl shadow-md shadow-slate-200/50 border-b border-slate-200'
          : 'bg-white/80 backdrop-blur-lg border-b border-slate-200/80'
      )}
    >
      <div className="page-container">
        <div className="flex justify-between items-center h-14 sm:h-16 md:h-[4.25rem]">
          {/* Logo */}
          <Link
            to={user ? '/dashboard' : '/'}
            className={cn(
              'flex items-center gap-2 sm:gap-3 group rounded-xl -ml-1.5 pl-1.5 pr-2 py-1.5',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2'
            )}
          >
            <motion.div
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.96 }}
              className="relative flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-blue-600 shadow-sm group-hover:shadow-md transition-shadow"
            >
              <div className="absolute inset-0 rounded-xl bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity" />
              <Rocket className="w-4 h-4 sm:w-5 sm:h-5 text-white relative z-10" strokeWidth={2.5} />
            </motion.div>
            <span className="text-lg sm:text-xl font-bold tracking-tight text-slate-900 group-hover:text-blue-600 transition-colors hidden sm:block">
              BuildxProject
            </span>
            <span className="text-lg font-bold tracking-tight text-blue-600 sm:hidden">
              BuildxProject
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {user ? (
              <>
                <div className="flex items-center gap-0.5 p-1 rounded-2xl bg-slate-100 border border-slate-200/80">
                  {navLinks.map((link) => {
                    const Icon = link.icon;
                    const isActive = location.pathname === link.path;
                    const showUnreadBadge = link.path === '/requests' && hasUnread;
                    return (
                      <Link
                        key={link.path}
                        to={link.path}
                        className={cn(
                          'relative flex items-center gap-2 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
                          isActive
                            ? 'text-white bg-blue-600 shadow-sm'
                            : 'text-slate-600 hover:text-blue-600 hover:bg-white'
                        )}
                      >
                        <span className="relative">
                          <Icon className={cn('w-4 h-4 shrink-0', isActive && 'text-white')} strokeWidth={2} />
                          {showUnreadBadge && (
                            <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full ring-2 ring-white" aria-hidden="true" />
                          )}
                        </span>
                        <span className="whitespace-nowrap">{link.label}</span>
                        {isActive && (
                          <motion.div
                            layoutId="navbarActive"
                            className="absolute inset-0 rounded-xl bg-blue-600 -z-10"
                            transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                          />
                        )}
                      </Link>
                    );
                  })}
                </div>

                <div className="flex items-center gap-2 ml-3 pl-3 border-l border-slate-200">
                  <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-left min-w-0">
                    <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center text-white font-semibold text-sm shrink-0">
                      {user.name?.charAt(0)?.toUpperCase() ?? 'U'}
                    </div>
                    <span className="text-sm font-medium text-slate-700 truncate max-w-[100px] lg:max-w-[140px] hidden sm:block">
                      {user.name?.split(' ')[0] ?? user.name}
                    </span>
                  </div>
                  <motion.button
                    type="button"
                    onClick={handleLogout}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={cn(
                      'flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium',
                      'text-gray-600 hover:text-red-600 hover:bg-red-50',
                      'border border-transparent hover:border-red-200',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-2',
                      'transition-colors duration-200'
                    )}
                  >
                    <LogOut className="w-4 h-4 shrink-0" />
                    <span className="hidden lg:inline">Logout</span>
                  </motion.button>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link
                  to="/login"
                  className={cn(
                    'px-4 py-2.5 text-sm font-medium rounded-xl',
                    'text-slate-600 hover:text-blue-600 hover:bg-slate-50',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
                    'transition-colors duration-200'
                  )}
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className={cn(
                  'inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl',
                  'bg-blue-600 text-white hover:bg-blue-700',
                  'shadow-sm hover:shadow-md',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
                    'transition-all duration-200'
                  )}
                >
                  Sign Up
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <motion.button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-expanded={mobileMenuOpen}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            className={cn(
              'md:hidden flex items-center justify-center w-10 h-10 rounded-xl',
              'text-slate-700 hover:text-blue-600 hover:bg-slate-100',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
              'transition-colors duration-200'
            )}
          >
            <AnimatePresence mode="wait">
              {mobileMenuOpen ? (
                <motion.div key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.15 }}>
                  <X className="w-6 h-6" strokeWidth={2} />
                </motion.div>
              ) : (
                <motion.div key="menu" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.15 }}>
                  <Menu className="w-6 h-6" strokeWidth={2} />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden fixed inset-0 z-40 bg-black/25 backdrop-blur-sm pt-14 sm:pt-16"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="md:hidden fixed left-0 right-0 z-50 bg-white border-b border-slate-200 shadow-xl rounded-b-2xl overflow-hidden top-14 sm:top-16 safe-top max-h-[calc(100dvh-4rem)] flex flex-col"
          >
            <div className="px-3 py-4 overflow-y-auto flex-1 min-h-0 safe-bottom">
              {user ? (
                <>
                  <p className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Menu</p>
                  <nav className="space-y-0.5">
                    {navLinks.map((link, index) => {
                      const Icon = link.icon;
                      const isActive = location.pathname === link.path;
                      const showUnreadBadge = link.path === '/requests' && hasUnread;
                      return (
                        <motion.div key={link.path} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.03 }}>
                          <Link
                            to={link.path}
                            onClick={() => setMobileMenuOpen(false)}
                            className={cn(
                              'flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium transition-colors',
                              isActive
                                ? 'bg-blue-600 text-white'
                                : 'text-slate-700 hover:bg-slate-100 active:bg-slate-200'
                            )}
                          >
                            <span className="relative">
                              <Icon className="w-5 h-5 shrink-0" strokeWidth={2} />
                              {showUnreadBadge && (
                                <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full ring-2 ring-white" aria-hidden="true" />
                              )}
                            </span>
                            <span className="flex-1">{link.label}</span>
                            <ChevronRight className={cn('w-4 h-4 shrink-0', isActive ? 'text-white/80' : 'text-gray-400')} />
                          </Link>
                        </motion.div>
                      );
                    })}
                  </nav>
                  <div className="mt-4 pt-4 border-t border-slate-200 space-y-2">
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-slate-100">
                      <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white font-semibold text-sm shrink-0">
                        {user.name?.charAt(0)?.toUpperCase() ?? 'U'}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-900 truncate">{user.name}</p>
                        <p className="text-xs text-slate-500 truncate">{user.email}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        handleLogout();
                        setMobileMenuOpen(false);
                      }}
                      className="w-full flex items-center justify-center gap-2 px-3 py-3 rounded-xl text-sm font-semibold text-red-600 bg-red-50 hover:bg-red-100 border border-red-200/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-2 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </div>
                </>
              ) : (
                <div className="space-y-2 px-2">
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl text-slate-700 font-semibold border border-slate-300 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 transition-colors"
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl font-semibold bg-blue-600 text-white hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 transition-all"
                  >
                    Sign Up
                  </Link>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
